import {
    type AllowedComponentProps,
    type PublicProps,
    type Component,
    type VNodeProps,
} from 'vue';

export type LayerComponentProps<C extends Component> = C extends new (
    ...args: any
) => any
    ? Omit<
          InstanceType<C>['$props'],
          keyof VNodeProps | keyof AllowedComponentProps | keyof PublicProps
      >
    : never;

interface LayerInfo {
    component: Component;
    props: any;
    id: string;
}

export type LayerDefaultProps = {
    LayerId?: string;
};

interface GenericLayerInfo<T extends Component> {
    component: T;
    props: LayerComponentProps<T> & LayerDefaultProps;
}

type FirstParam<T> = T extends [] ? void : T extends [arg1: infer V] ? V : void;

export type LayerReturnType<C extends Component> = FirstParam<
    Parameters<LayerComponentProps<C>['onClose']>
>;

interface LayerHandlerInfo extends LayerInfo {
    close: (resolveParams?: any) => void;
    error: (rejectParams?: any) => void;
}

export interface LayerInfoGeneric<T extends Component> {
    component: T;
    props: LayerComponentProps<T>;
}

export default class PageOverlayModule {
    private layers: LayerHandlerInfo[] = [];

    get Layers() {
        return this.layers;
    }

    get lastLayer() {
        return this.layers[this.layers.length - 1];
    }

    private idGenerator() {
        return Date.now().toString(36) + Math.random().toString(36);
    }

    private closeLayerById(layerId: string) {
        this.layers = this.layers.filter(({ id }) => id !== layerId);
    }

    addLayer<T extends Component>(layerInfo: Omit<GenericLayerInfo<T>, 'id'>) {
        const layerId = this.idGenerator();
        this.layers.push({
            ...layerInfo,
            id: layerId,
            close: () => this.closeLayerById(layerId),
            error: () => this.closeLayerById(layerId),
        });
    }

    addAsyncLayer<T extends Component>(
        layerInfo: Omit<GenericLayerInfo<T>, 'id'>
    ): Promise<LayerReturnType<T>> {
        return new Promise((resolve, reject) => {
            const layerId = this.idGenerator();
            this.layers.push({
                ...layerInfo,
                id: layerId,
                close: (resolveParams: any) => {
                    this.closeLayerById(layerId);
                    resolve(resolveParams);
                },
                error: (rejectParams: any) => {
                    this.closeLayerById(layerId);
                    reject(new Error(rejectParams));
                },
            });
        });
    }

    closeLayer({
        layerId,
        rejectParams,
    }: {
        layerId: string;
        rejectParams?: any;
    }) {
        this.layers.find(({ id }) => id !== layerId)?.error?.(rejectParams);
    }

    closeLastLayer() {
        const lastLayerId = this.lastLayer?.id;
        if (lastLayerId) {
            this.lastLayer.error?.(undefined);
        }
    }

    closeAll() {
        this.layers = [];
    }
}
